import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { formatToReal } from "../../../utils/utils";
import { useIsPrintExport } from "../../../contexts/PrintExportContext";

const Protestos = ({
  protestos = [],
  quantidadeTotal,
  primeiroValor,
  valorTotal,
  temEstado = true,
  temVencimento = true,
  isNacional = false,
}) => {
  const [expandidoUser, setExpandido] = useState(false);
  const isPrintExport = useIsPrintExport();
  const expandido = expandidoUser || isPrintExport;

  const LIMITE = 10;
  const total = protestos.length;
  const restantes = Math.max(total - LIMITE, 0);

  const listaExibida = useMemo(
    () => (expandido ? protestos : protestos.slice(0, LIMITE)),
    [expandido, protestos]
  );

  const exibirAnuencia =
    Array.isArray(protestos) &&
    protestos.some((p) => p?.temAnuencia !== undefined);

  let gridClass = 5;

  if (exibirAnuencia) gridClass += 1;
  if (temEstado) gridClass += 1;
  if (temVencimento) gridClass += 1;

  const gridColsClass =
    {
      5: "grid-cols-5",
      6: "grid-cols-6",
      7: "grid-cols-7",
      8: "grid-cols-8",
    }[gridClass] || "grid-cols-5";

  return (
    <>
      {/* Resumo */}
      <div className="grid gap-3 mb-5 grid-cols-3">
        {quantidadeTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Quantidade total
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {quantidadeTotal}
            </div>
          </div>
        )}

        {primeiroValor && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Primeiro valor
            </div>
            <div className="text-lg font-semibold text-rose-600">
              {formatToReal(primeiroValor)}
            </div>
          </div>
        )}

        {valorTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Valor total
            </div>
            <div className="text-lg font-semibold text-rose-600">
              {formatToReal(valorTotal)}
            </div>
          </div>
        )}
      </div>

      {/* Tabela */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div
            className={`grid ${gridColsClass} gap-4 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500`}
          >
            <div>Data</div>
            <div className="col-span-2">Cartório</div>
            <div>Cidade</div>
            <div>Estado</div>
            {temVencimento && <div>Vencimento</div>}
            {exibirAnuencia && <div>Anuência</div>}
            <div className="text-right">Valor</div>
          </div>
        </div>

        {/* Linhas */}
        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          {listaExibida.map((protesto, index) => (
            <div
              key={index}
              className={`grid ${gridColsClass} items-center gap-4 px-4 py-3 text-sm transition-colors ${
                index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
              } hover:bg-slate-50`}
            >
              <div className="font-medium text-slate-700">
                {protesto?.data || "Não Divulgado"}
              </div>

              <div
                className="col-span-2 font-medium text-slate-900 truncate"
                title={
                  protesto?.cartorio != null ? String(protesto.cartorio) : ""
                }
              >
                {protesto?.cartorio != null &&
                String(protesto.cartorio).trim() !== ""
                  ? typeof protesto.cartorio === "string" &&
                    /^\d+$/.test(protesto.cartorio.trim())
                    ? `${parseInt(protesto.cartorio.trim(), 10)}º cartório`
                    : protesto.cartorio
                  : "-"}
              </div>
              <div className="text-slate-700">{protesto?.cidade || "-"}</div>
              <div className="text-slate-700">{protesto?.estado || "-"}</div>
              {temVencimento && (
                <div className="text-slate-700">
                  {protesto?.vencimento || "-"}
                </div>
              )}
              {exibirAnuencia && (
                <div className="text-slate-700">
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                    {protesto?.temAnuencia ? "Sim" : "Não"}
                  </span>
                </div>
              )}
              <div className="text-right font-semibold text-rose-600 tabular-nums">
                {formatToReal(protesto?.valor)}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-slate-500">(+{restantes} restantes)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {!expandido && total > LIMITE && (
        <div className="text-sm mt-3 text-slate-500 text-center">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
      {isNacional && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          Atenção: Em alguns casos, o Protesto Nacional não exibe a data do
          protesto porque o cartório de origem não disponibilizou essa
          informação.
        </div>
      )}
    </>
  );
};

export default Protestos;
